import { trackCalculatorInit, useTrackCalculatorLoad } from '@hex/shared/tracking';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CURRENT_INTEREST, MAXIMUM_INTEREST, MINIMUM_INTEREST } from './constants';
import { LoanCalculatorForm } from './types';
import { formatNumber } from './utils';

export const useCostForm = () => {
  const { t } = useTranslation();

  const getAmount = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const amount = urlParams.get('amount');

    if (amount) {
      return parseInt(amount);
    }

    return undefined;
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, submitCount, isDirty, dirtyFields },
  } = useForm<LoanCalculatorForm>({
    shouldFocusError: true,
    defaultValues: {
      loanAmount: getAmount(),
      maturity: 30,
      interest: formatNumber(CURRENT_INTEREST, true),
      repaymentMethod: 'Annuity',
      loanRepaymentSecurity: false,
      lsiLevel: '100%',
      insuredPeople: '1',
      firstAge: undefined,
      secondAge: undefined,
    },
  });

  const allFields = watch();

  const maturityFieldRules = {
    required: t('form.validation.maturity.required'),
    min: {
      value: 1,
      message: t('form.validation.maturity.limit', { min: 1, max: 40 }),
    },
    max: {
      value: 40,
      message: t('form.validation.maturity.limit', { min: 1, max: 40 }),
    },
  };

  const interestFieldRules = {
    required: t('form.validation.interest.required'),
    min: {
      value: 0.5,
      message: t('form.validation.interest.limit', {
        min: formatNumber(MINIMUM_INTEREST, true),
        max: formatNumber(MAXIMUM_INTEREST, true),
      }),
    },
    max: {
      value: 15,
      message: t('form.validation.interest.limit', {
        min: formatNumber(MINIMUM_INTEREST, true),
        max: formatNumber(MAXIMUM_INTEREST, true),
      }),
    },
  };

  const [isTracked, setIsTracked] = useState(false);
  const trackCalculatorStarted = () => {
    if (!isTracked) {
      trackCalculatorInit();
      setIsTracked(true);
    }
  };

  // focus on error functionality
  useEffect(() => {
    if (errors) {
      // errors need to be sorted according to keys in the form data type,
      // by default they are sorted in the order they were registered by RHF
      const order: Record<keyof LoanCalculatorForm, number> = {
        loanAmount: 1,
        maturity: 2,
        interest: 3,
        repaymentMethod: 4,
        loanRepaymentSecurity: 5,
        lsiLevel: 6,
        insuredPeople: 7,
        firstAge: 8,
        secondAge: 9,
      };
      const firstError = Object.keys(errors).sort(
        (a, b) => order[a as keyof LoanCalculatorForm] - order[b as keyof LoanCalculatorForm],
      )[0];

      if (firstError === 'firstAge' || firstError === 'secondAge') {
        const el = document.getElementById(firstError);
        const inputs = el?.getElementsByTagName('button');

        if (inputs && inputs[0]) inputs[0].focus();
      } else {
        document.getElementsByName(firstError)[0]?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  useTrackCalculatorLoad('Cost Calculator');

  return {
    control,
    handleSubmit,
    trackCalculatorStarted,
    allFields,
    maturityFieldRules,
    interestFieldRules,
    errors,
    isDirty,
    dirtyFields,
  };
};
